import type { ApiError, ApiResponse } from '@crm/shared'

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8787'

/** Mirrors the API's CSRF middleware — a header no cross-site form can set. */
const CSRF_HEADER = 'X-Requested-With'
const CSRF_VALUE = 'crm-web'

export class ApiClientError extends Error {
  readonly code: ApiError['code']
  readonly status: number
  readonly fields?: Record<string, string[]>

  constructor(status: number, error: ApiError) {
    super(error.message)
    this.name = 'ApiClientError'
    this.status = status
    this.code = error.code
    this.fields = error.fields
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set(CSRF_HEADER, CSRF_VALUE)
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')

  let res: Response
  try {
    res = await fetch(`${API_URL}${path}`, {
      ...init,
      headers,
      // The session cookie lives on the API origin; without this it is never sent.
      credentials: 'include',
    })
  } catch {
    throw new ApiClientError(0, {
      code: 'internal_error',
      message: 'Cannot reach the server. Check your connection.',
    })
  }

  // 204s and empty bodies are legitimate; don't try to parse them.
  const text = await res.text()
  const body = (text ? JSON.parse(text) : {}) as ApiResponse<T>

  if (!res.ok || body.error) {
    throw new ApiClientError(
      res.status,
      body.error ?? { code: 'internal_error', message: 'Request failed' },
    )
  }

  return body.data as T
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
}
